import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Checkbox } from '@ovComponents/2-component/checkbox/checkbox';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import type { ColumnDef } from '@tanstack/react-table';
import {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTradingMutation } from '../hooks/useTradingMutation';
import { SortIndicator, useTradingTable } from '../hooks/useTradingTable';
import { FETCH_PORTFOLIO_REPORTS } from '../queries';
import {
  BOLD_SX,
  BORDERED_BOX_SX,
  COMPACT_SELECT_FIELD_SX,
  COMPACT_TEXT_FIELD_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  SORTABLE_HEADER_SX,
  TABULAR_SX,
} from '../styles';
import { useTradingHub } from '../tradingHubContext';
import type { PortfolioReport } from '../types';
import {
  calculateAggregateExposure,
  calculateDrift,
  calculateModelAggregation,
  classifyPriority,
  DENSITY_CONFIG,
  exportPortfoliosToCsv,
  getPriorityChips,
} from '../utils';
import { PORTFOLIO_HEADER_COLS, VIEW_MODE_TOGGLES } from '../utils/constants';
import {
  BUCKET_SUB_TRADE_REQUESTS,
  CREATE_SUB_TRADE_REQUESTS,
  RUN_PORTFOLIO_OPTIMIZER,
} from '../utils/mutations';
import {
  getPaginationRequestKey,
  getServerPagination,
  isPaginationSettled,
  isPortfolioPageLoading,
  isPortfolioPageReady,
  sliceClientPage,
} from '../utils/portfolioTabPagination';
import BulkTradeStaging from './bulkTradeStaging';
import CrossPortfolioExposure from './crossPortfolioExposure';
import ModelPortfolioView from './modelPortfolioView';
import PortfolioRow, {
  getPriorityConfig,
  type PriorityKey,
} from './portfolioRow';
import ReviewAndCommit from './reviewAndCommit';

const PortfoliosTab = () => {
  const { sys } = useThemeTokens();
  const { activeOrganization } = useContext(UserContext);
  const {
    density,
    refreshKey,
    portfolioStatusPreFilter,
    setPortfolioStatusPreFilter,
    setActiveFlowStep,
    setCurrentTab,
    triggerRefresh,
    pinnedPortfolioDeepLink,
    setPinnedPortfolioDeepLink,
    setLastBulkCreationTime,
  } = useTradingHub();
  const densityConfig = DENSITY_CONFIG[density];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ANY');
  const [portfolioTypeFilter, setPortfolioTypeFilter] = useState('ANY');
  const [modelFilter, setModelFilter] = useState('ANY');
  const [currencyFilter, setCurrencyFilter] = useState('ANY');
  const [selectedPortfolios, setSelectedPortfolios] = useState<Set<string>>(
    new Set(),
  );
  const [expandedPortfolioId, setExpandedPortfolioId] = useState<string | null>(
    null,
  );
  const skipSearchPageResetRef = useRef(false);

  const createFilterChange =
    (setter: (v: any) => void, transform?: (v: string) => string | number) =>
    (e: any) => {
      startTransition(() => {
        setter(transform ? transform(e.target.value) : e.target.value);
        setPage(1);
      });
    };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (skipSearchPageResetRef.current) {
      skipSearchPageResetRef.current = false;
      return;
    }
    startTransition(() => setPage(1));
  }, [debouncedSearch]);

  // Consume pre-filter from cross-tab navigation (e.g. "View flagged portfolios" from insights)
  useEffect(() => {
    if (portfolioStatusPreFilter) {
      const filter = portfolioStatusPreFilter;
      setPortfolioStatusPreFilter(null);
      startTransition(() => {
        setStatusFilter(filter);
        setPageSize(100);
        setPage(1);
      });
    }
  }, [portfolioStatusPreFilter, setPortfolioStatusPreFilter]);

  const dismissPinnedDeepLink = useCallback(() => {
    setPinnedPortfolioDeepLink(null);
  }, [setPinnedPortfolioDeepLink]);

  const scrolledPinRef = useRef<string | null>(null);
  const fetchedPinRef = useRef<string | null>(null);
  const pinnedHydrationWarnedRef = useRef<string | null>(null);

  const [fetchPinnedReport, { data: pinnedReportData }] = useLazyQuery(
    FETCH_PORTFOLIO_REPORTS,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  );

  const handleSetExpanded = useCallback(
    (id: string | null) => {
      setExpandedPortfolioId(id);
      if (
        pinnedPortfolioDeepLink &&
        id !== pinnedPortfolioDeepLink.portfolioReportId
      ) {
        dismissPinnedDeepLink();
      }
    },
    [pinnedPortfolioDeepLink, dismissPinnedDeepLink],
  );

  // Consume a drift-queue deep-link: reset filters, jump to page 1, expand, and
  // keep the pinned snapshot alive until the user dismisses it.
  useEffect(() => {
    if (!pinnedPortfolioDeepLink) return;

    setExpandedPortfolioId(pinnedPortfolioDeepLink.portfolioReportId);

    if (debouncedSearch !== '') {
      skipSearchPageResetRef.current = true;
    }
    startTransition(() => {
      setStatusFilter('ANY');
      setPortfolioTypeFilter('ANY');
      setModelFilter('ANY');
      setCurrencyFilter('ANY');
      setSearchTerm('');
      setViewMode('portfolios');
      setPage(1);
    });
  }, [pinnedPortfolioDeepLink]);

  // UI view state
  const [viewMode, setViewMode] = useState<'portfolios' | 'exposure' | 'model'>(
    'portfolios',
  );
  const [showReviewCommit, setShowReviewCommit] = useState(false);
  const [showBulkStaging, setShowBulkStaging] = useState(false);

  const { showToast } = useGlobalToast();
  const { t } = useTranslation('portfolioTradingHub');
  const [runOptimizer] = useTradingMutation(RUN_PORTFOLIO_OPTIMIZER);
  const [createSubTradeRequestsFromExposure] = useTradingMutation(
    CREATE_SUB_TRADE_REQUESTS,
  );
  const [bucketSubTradeRequestsFromExposure] = useTradingMutation(
    BUCKET_SUB_TRADE_REQUESTS,
  );

  const handleCreateTradesFromExposure = useCallback(
    async (
      selections: Array<{
        ticker: string;
        portfolioId: string;
        portfolioReportId: string;
        portfolioName: string;
        subAccountId: string;
        financialProductId: string;
        currency: string;
        valueCents: number;
        quantity: number;
        expectedPercentage: number;
        expectedValueCents: number;
        cashAvailableForTradeCents?: number;
        modelPortfolioType?: string;
        objectId?: string;
      }>,
      direction: 'SELL' | 'BUY' | 'REBALANCE',
      buyAmountCentsPerAccount?: number,
    ) => {
      try {
        const actionableTrades = selections
          .map((sel) => {
            let type: string;
            let amountCents: number;

            if (direction === 'SELL') {
              type = 'SELL';
              amountCents = sel.valueCents;
            } else if (direction === 'BUY') {
              type = 'BUY';
              amountCents = buyAmountCentsPerAccount ?? 0;
              if (amountCents <= 0) return null;
            } else {
              const diff = sel.expectedValueCents - sel.valueCents;
              if (Math.abs(diff) < 100) return null;
              type = diff > 0 ? 'BUY' : 'SELL';
              amountCents = Math.abs(diff);
            }

            const isGoal = sel.modelPortfolioType === 'GOAL';
            return {
              portfolioId: sel.portfolioId,
              source: isGoal ? ('GOAL' as const) : ('SUB_ACCOUNT' as const),
              sourceId: isGoal
                ? sel.objectId || sel.subAccountId
                : sel.subAccountId,
              subAccountId: sel.subAccountId,
              financialProductId: sel.financialProductId,
              portfolioReportId: sel.portfolioReportId,
              type,
              moneyAllocatedCents: Math.round(amountCents),
              currency: sel.currency,
              cashAvailableForTradeCents: sel.cashAvailableForTradeCents ?? 0,
            };
          })
          .filter(Boolean) as Array<{
          portfolioId: string;
          source: 'GOAL' | 'SUB_ACCOUNT';
          sourceId: string;
          subAccountId: string;
          financialProductId: string;
          portfolioReportId: string;
          type: string;
          moneyAllocatedCents: number;
          currency: string;
          cashAvailableForTradeCents: number;
        }>;

        // Cap BUY amounts per portfolio against available cash + sell proceeds
        const portfolioStats = new Map<
          string,
          { totalBuyCents: number; totalSellCents: number; cashCents: number }
        >();
        for (const t of actionableTrades) {
          const stats = portfolioStats.get(t.portfolioId) ?? {
            totalBuyCents: 0,
            totalSellCents: 0,
            cashCents: t.cashAvailableForTradeCents,
          };
          if (t.type === 'BUY') stats.totalBuyCents += t.moneyAllocatedCents;
          else stats.totalSellCents += t.moneyAllocatedCents;
          portfolioStats.set(t.portfolioId, stats);
        }
        for (const t of actionableTrades) {
          if (t.type !== 'BUY') continue;
          const stats = portfolioStats.get(t.portfolioId);
          if (!stats || stats.totalBuyCents === 0) continue;
          const buyingPower = stats.cashCents + stats.totalSellCents;
          if (stats.totalBuyCents > buyingPower) {
            const scale = Math.max(0, buyingPower / stats.totalBuyCents);
            t.moneyAllocatedCents = Math.round(t.moneyAllocatedCents * scale);
          }
        }
        const cashCappedTrades = actionableTrades.filter(
          (t) => t.moneyAllocatedCents > 0,
        );

        if (cashCappedTrades.length === 0) {
          showToast({
            severity: 'info',
            message:
              'No actionable trades — all selected positions are already at or near target.',
          });
          return;
        }

        // Group by source key — backend requires source+sourceId per call (legacy BulkTradeRequest mechanism)
        const tradesBySource = new Map<
          string,
          {
            source: 'GOAL' | 'SUB_ACCOUNT';
            sourceId: string;
            trades: typeof cashCappedTrades;
          }
        >();
        for (const t of cashCappedTrades) {
          const key = `${t.source}:${t.sourceId}`;
          const group = tradesBySource.get(key) ?? {
            source: t.source,
            sourceId: t.sourceId,
            trades: [],
          };
          group.trades.push(t);
          tradesBySource.set(key, group);
        }

        for (const { source, sourceId, trades } of Array.from(
          tradesBySource.values(),
        )) {
          const subTradeRequests = trades.map(
            ({
              subAccountId,
              financialProductId,
              portfolioReportId,
              type,
              moneyAllocatedCents,
              currency,
            }) => ({
              subAccountId,
              financialProductId,
              portfolioReportId,
              type,
              moneyAllocatedCents,
              currency,
            }),
          );
          await createSubTradeRequestsFromExposure({
            variables: { input: { source, sourceId, subTradeRequests } },
          });
        }

        if (activeOrganization?.bulkTradingProcess === 'BUCKET') {
          for (const { source, sourceId } of Array.from(
            tradesBySource.values(),
          )) {
            try {
              await bucketSubTradeRequestsFromExposure({
                variables: {
                  input: {
                    source,
                    sourceId,
                    ...(activeOrganization?.id && {
                      organizationId: activeOrganization.id,
                    }),
                  },
                },
              });
            } catch {
              // Non-critical: trades were still created
            }
          }
        }

        showToast({
          severity: 'success',
          message: `Created ${cashCappedTrades.length} trade request${cashCappedTrades.length !== 1 ? 's' : ''} across ${tradesBySource.size} account${tradesBySource.size !== 1 ? 's' : ''}. View in Trade Pipeline.`,
        });
        triggerRefresh();
      } catch {
        // Error toast handled by useTradingMutation
      }
    },
    [
      activeOrganization,
      createSubTradeRequestsFromExposure,
      bucketSubTradeRequestsFromExposure,
      triggerRefresh,
    ],
  );

  const handleToggleSelect = useCallback((reportId: string) => {
    setSelectedPortfolios((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  }, []);

  const listQueryVariables = useMemo(() => {
    const serverPagination = getServerPagination({
      page,
      pageSize,
      isClientSearch: Boolean(debouncedSearch),
    });
    return {
      input: {
        filter: {
          organizationId: activeOrganization?.id,

          driftRebalanceRequired: statusFilter === 'DRIFT' ? true : undefined,
          transferRebalanceRequired:
            statusFilter === 'TRANSFER' ? true : undefined,
          liquidateRebalanceRequired:
            statusFilter === 'LIQUIDATE' ? true : undefined,
          anyRebalanceRequired:
            statusFilter === 'ANY_REBALANCE' ? true : undefined,
          pendingSubTradeRequests:
            statusFilter === 'PENDING_TRADES' ? true : undefined,

          modelPortfolioType:
            portfolioTypeFilter !== 'ANY' ? portfolioTypeFilter : undefined,
        },
        pagination: {
          sortField: 'updatedAt',
          sortDesc: true,
          ...serverPagination,
        },
      },
    };
  }, [
    activeOrganization?.id,
    statusFilter,
    portfolioTypeFilter,
    debouncedSearch,
    page,
    pageSize,
  ]);

  const { error, data, previousData, refetch, networkStatus } = useQuery(
    FETCH_PORTFOLIO_REPORTS,
    {
      fetchPolicy: 'cache-and-network',
      // Re-hit the network when page/size changes — cache-first was serving stale
      // empty pages after variable transitions.
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      // Some portfolios reference sub-accounts the backend can't resolve
      // ("Sub Account does not exist"). With the default 'none' policy a single
      // such error blanks the whole tab; 'all' lets the rest of the list render
      // and we drop the null entries below.
      errorPolicy: 'all',
      variables: listQueryVariables,
      skip: !activeOrganization?.id, // Don't query until we have an org
    },
  );

  useEffect(() => {
    if (refreshKey > 0) refetch();
  }, [refreshKey]);

  const paginationRequestKey = useMemo(
    () =>
      getPaginationRequestKey({
        page,
        pageSize,
        isClientSearch: Boolean(debouncedSearch),
      }),
    [page, pageSize, debouncedSearch],
  );
  const [settledPaginationKey, setSettledPaginationKey] = useState<
    string | null
  >(null);
  const paginationRequestKeyRef = useRef(paginationRequestKey);
  paginationRequestKeyRef.current = paginationRequestKey;

  // Advance settled key only when Apollo transitions to `ready`, not when the
  // request key changes while status is still `ready` from the previous page.
  useEffect(() => {
    if (!isPortfolioPageReady(networkStatus)) return;
    setSettledPaginationKey(paginationRequestKeyRef.current);
  }, [networkStatus]);

  const isPageSettled = isPaginationSettled(
    paginationRequestKey,
    settledPaginationKey,
  );
  const isPageLoading = isPortfolioPageLoading(networkStatus) || !isPageSettled;
  const isPageReady = isPortfolioPageReady(networkStatus) && isPageSettled;
  const canRenderPortfolios =
    isPageSettled &&
    (networkStatus === NetworkStatus.ready ||
      networkStatus === NetworkStatus.refetch);

  const queryPageData = data?.fetchPortfolioReports;
  const rawPortfolios = queryPageData?.portfolioReports;
  const unresolvedRowCount = useMemo(() => {
    if (!rawPortfolios) return 0;
    return rawPortfolios.filter((row: unknown) => row == null).length;
  }, [rawPortfolios]);
  const portfolios = useMemo(() => {
    if (!canRenderPortfolios || !rawPortfolios) return [];
    return rawPortfolios.filter(Boolean);
  }, [rawPortfolios, canRenderPortfolios]);

  // Hydrate a deep-linked portfolio with full list-query fields when it is not
  // on the current paginated page (goalId / subAccountId filter, perPage 1).
  useEffect(() => {
    if (!pinnedPortfolioDeepLink || !activeOrganization?.id) {
      fetchedPinRef.current = null;
      return;
    }
    const pinId = pinnedPortfolioDeepLink.portfolioReportId;
    if (portfolios.some((p: { id: string }) => p.id === pinId)) {
      fetchedPinRef.current = null;
      return;
    }
    if (fetchedPinRef.current === pinId) return;

    const pinned = pinnedPortfolioDeepLink.report;
    const objectId = pinned.object?.id;
    if (!objectId) {
      if (pinnedHydrationWarnedRef.current !== pinId) {
        pinnedHydrationWarnedRef.current = pinId;
        showToast({
          severity: 'warning',
          message: t('deepLink.missingObjectId'),
        });
      }
      return;
    }
    pinnedHydrationWarnedRef.current = null;

    fetchedPinRef.current = pinId;
    const isGoal = pinned.modelPortfolioType === 'GOAL';
    fetchPinnedReport({
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization.id,
            ...(isGoal ? { goalId: objectId } : { subAccountId: objectId }),
          },
          pagination: { perPage: 1, offSet: 0 },
        },
      },
    });
  }, [
    pinnedPortfolioDeepLink,
    activeOrganization?.id,
    portfolios,
    fetchPinnedReport,
    showToast,
    t,
  ]);

  // The list query asks for non-nullable fields (e.g. `holdings.financialProduct.id`).
  // When the backend returns null for one of these on any row, GraphQL's non-null
  // propagation nullifies the entire `fetchPortfolioReports` payload, even the
  // good rows. Detect that explicitly so we surface the real error instead of a
  // misleading "No portfolios found".
  const pageGraphQLErrors = useMemo(() => {
    const errs = error?.graphQLErrors ?? [];
    return errs.map((e) => e.message);
  }, [error]);
  const isPagePayloadMissing =
    isPageReady && data !== undefined && queryPageData == null;
  const hasPageLoadError =
    isPageReady && (pageGraphQLErrors.length > 0 || isPagePayloadMissing);
  const totalCount =
    queryPageData?.totalCount ??
    previousData?.fetchPortfolioReports?.totalCount ??
    0;

  // Client-side search filter + priority enrichment
  // Uses debouncedSearch to avoid cascading re-renders (filtering, priority, exposure, table) on every keystroke
  const filteredPortfolios = useMemo(() => {
    let result = portfolios;

    if (modelFilter !== 'ANY') {
      result = result.filter((report: any) => {
        const modelId = report.modelPortfolio?.id || '__unassigned__';
        return modelId === modelFilter;
      });
    }

    if (currencyFilter !== 'ANY') {
      result = result.filter((report: any) => {
        if (report.baseCurrency === currencyFilter) return true;
        return (report.originalCurrencies || []).includes(currencyFilter);
      });
    }

    if (debouncedSearch) {
      const lowerSearch = debouncedSearch.toLowerCase();
      result = result.filter((report: any) => {
        if (report.modelPortfolioType === 'GOAL') {
          const goalName = report.object?.name?.toLowerCase() || '';
          const userName =
            `${report.object?.user?.firstName || ''} ${report.object?.user?.lastName || ''}`.toLowerCase();
          const entityName =
            report.object?.user?.entityName?.toLowerCase() || '';
          const household =
            report.object?.householdClientGroup?.name?.toLowerCase() || '';
          return (
            goalName.includes(lowerSearch) ||
            userName.includes(lowerSearch) ||
            entityName.includes(lowerSearch) ||
            household.includes(lowerSearch)
          );
        }

        const user = report.object?.account?.user;
        const fullName =
          `${user?.firstName || ''} ${user?.lastName || ''}`.toLowerCase();
        const entityName = user?.entityName?.toLowerCase() || '';
        const accountType = report.object?.account?.type?.toLowerCase() || '';
        const goalName = report.object?.goal?.name?.toLowerCase() || '';
        const household =
          report.object?.goal?.householdClientGroup?.name?.toLowerCase() || '';
        return (
          fullName.includes(lowerSearch) ||
          entityName.includes(lowerSearch) ||
          accountType.includes(lowerSearch) ||
          goalName.includes(lowerSearch) ||
          household.includes(lowerSearch)
        );
      });
    }
    return result.map((report: any) => {
      const { priority, rank } = classifyPriority(report);
      const drift = calculateDrift(report) ?? 0;
      return {
        ...report,
        _priority: priority,
        _priorityRank: rank,
        _drift: drift,
      };
    });
  }, [portfolios, debouncedSearch, modelFilter, currencyFilter]);

  const displayedPortfolios = useMemo(() => {
    if (debouncedSearch) {
      return sliceClientPage(filteredPortfolios, page, pageSize);
    }
    return filteredPortfolios;
  }, [filteredPortfolios, debouncedSearch, page, pageSize]);

  const totalPages = debouncedSearch
    ? Math.max(1, Math.ceil(filteredPortfolios.length / pageSize))
    : Math.max(1, Math.ceil(totalCount / pageSize));

  const priorityCounts = useMemo(() => {
    const counts = { urgent: 0, review: 0, ok: 0 };
    for (const r of filteredPortfolios) {
      const p = r._priority as PriorityKey;
      if (p in counts) counts[p]++;
    }
    return counts;
  }, [filteredPortfolios]);

  const modelOptions = useMemo(() => {
    const seen = new Map<string, string>();
    portfolios.forEach((r: any) => {
      const id = r.modelPortfolio?.id || '__unassigned__';
      const name = r.modelPortfolio?.translatedName?.en || 'Unassigned';
      if (!seen.has(id)) seen.set(id, name);
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [portfolios]);

  const currencyOptions = useMemo(() => {
    const ccys = new Set<string>();
    portfolios.forEach((r: any) => {
      if (r.baseCurrency) ccys.add(r.baseCurrency);
      (r.originalCurrencies || []).forEach((c: string) => ccys.add(c));
    });
    return Array.from(ccys).sort();
  }, [portfolios]);

  const [activePriorityFilter, setActivePriorityFilter] = useState<
    PriorityKey | 'ALL'
  >('ALL');

  const portfolioColumns: ColumnDef<any>[] = useMemo(
    () => [
      { id: 'select', header: '', enableSorting: false },
      { id: 'expand', header: '', enableSorting: false },
      {
        id: 'priority',
        header: 'PRI',
        accessorFn: (row: any) => row._priorityRank,
        filterFn: (row: any, _columnId: string, filterValue: number) =>
          row.original._priorityRank === filterValue,
      },
      {
        id: 'name',
        header: 'Portfolio',
        accessorFn: (row: any) => {
          if (row.modelPortfolioType === 'GOAL')
            return row.object.name || 'Unnamed Goal';
          const user = row.object.account?.user;
          return user ? `${user.firstName} ${user.lastName}` : 'Unknown';
        },
      },
      {
        id: 'cash',
        header: 'Cash / Target',
        accessorFn: (row: any) => row.cashAvailableForTradeCents || 0,
      },
      {
        id: 'marketValue',
        header: 'Market Value',
        accessorFn: (row: any) => row.marketValueCents || 0,
      },
      {
        id: 'drift',
        header: 'Drift',
        accessorFn: (row: any) => row._drift ?? 0,
      },
      {
        id: 'trades',
        header: 'Trades',
        accessorFn: (row: any) => row.pendingSubTradeRequests || 0,
      },
      { id: 'actions', header: 'Actions', enableSorting: false },
    ],
    [],
  );

  const {
    table: portfolioTable,
    sorting,
    columnFilters,
    setColumnFilters,
  } = useTradingTable({
    data: displayedPortfolios,
    columns: portfolioColumns,
    enableFiltering: true,
  });

  const handlePriorityFilter = (priority: PriorityKey | 'ALL') => {
    setActivePriorityFilter(priority);
    if (priority === 'ALL') {
      setColumnFilters((prev) => prev.filter((f) => f.id !== 'priority'));
    } else {
      setColumnFilters((prev) => [
        ...prev.filter((f) => f.id !== 'priority'),
        { id: 'priority', value: getPriorityConfig(sys)[priority].rank },
      ]);
    }
  };

  const handleClearFilters = useCallback(() => {
    startTransition(() => {
      setSearchTerm('');
      setStatusFilter('ANY');
      setPortfolioTypeFilter('ANY');
      setModelFilter('ANY');
      setCurrencyFilter('ANY');
      setActivePriorityFilter('ALL');
      setColumnFilters((prev) => prev.filter((f) => f.id !== 'priority'));
      setPage(1);
    });
  }, [setColumnFilters]);

  const allRows = portfolioTable.getRowModel().rows;
  const visibleRowCount = allRows.length;

  // Progressive rendering: paint a small initial batch, then drip-feed the rest
  // with real setTimeout delays so the browser stays responsive between batches.
  const INITIAL_BATCH = 8;
  const BATCH_SIZE = 6;
  const BATCH_DELAY_MS = 60;
  const [renderedCount, setRenderedCount] = useState(INITIAL_BATCH);
  const progressiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const sortingKey = JSON.stringify(sorting);
  const filtersKey = JSON.stringify(columnFilters);
  const dataLen = displayedPortfolios.length;
  const dataFirstId = displayedPortfolios[0]?.id;
  const dataKey = `${dataLen}:${dataFirstId}:${sortingKey}:${filtersKey}:${page}:${pageSize}`;

  const prevDataKeyRef = useRef(dataKey);
  let effectiveRenderCount = renderedCount;
  if (prevDataKeyRef.current !== dataKey) {
    effectiveRenderCount = INITIAL_BATCH;
  }

  useEffect(() => {
    if (progressiveTimerRef.current) clearTimeout(progressiveTimerRef.current);
    prevDataKeyRef.current = dataKey;
    setRenderedCount(INITIAL_BATCH);
  }, [dataKey]);

  useEffect(() => {
    if (renderedCount < visibleRowCount) {
      progressiveTimerRef.current = setTimeout(() => {
        startTransition(() => {
          setRenderedCount((prev) =>
            Math.min(prev + BATCH_SIZE, visibleRowCount),
          );
        });
      }, BATCH_DELAY_MS);
      return () => {
        if (progressiveTimerRef.current)
          clearTimeout(progressiveTimerRef.current);
      };
    }
  }, [renderedCount, visibleRowCount]);

  // Keep an expanded row mounted even when it sits beyond the progressive-
  // render cutoff so the user can interact with it immediately.
  const rowsToRender = useMemo(() => {
    if (expandedPortfolioId) {
      const idx = allRows.findIndex(
        (r) => r.original.id === expandedPortfolioId,
      );
      if (idx >= 0 && idx >= effectiveRenderCount) {
        return allRows.slice(0, idx + 1);
      }
    }
    return allRows.slice(0, effectiveRenderCount);
  }, [allRows, effectiveRenderCount, expandedPortfolioId]);

  // Ghost row: when the deep-link target is not on the current server page,
  // synthesise a row from the dashboard snapshot pinned in context.
  const hydratedPinnedReport = useMemo(() => {
    if (!pinnedPortfolioDeepLink) return null;
    const expectedId = pinnedPortfolioDeepLink.portfolioReportId;
    const rows = pinnedReportData?.fetchPortfolioReports?.portfolioReports;
    if (!rows?.length) return null;
    const match = rows.find(
      (row: PortfolioReport | null) => row?.id === expectedId,
    );
    return (match ?? null) as PortfolioReport | null;
  }, [pinnedReportData, pinnedPortfolioDeepLink]);

  const ghostReport = useMemo(() => {
    if (!pinnedPortfolioDeepLink) return null;
    const pinned = pinnedPortfolioDeepLink.report;
    if (expandedPortfolioId !== pinnedPortfolioDeepLink.portfolioReportId) {
      return null;
    }
    // Already present in the loaded page — expand the real row instead.
    if (portfolios.some((p: { id: string }) => p.id === pinned.id)) return null;

    const fullReport = hydratedPinnedReport;
    const reportForPriority: PortfolioReport =
      fullReport ??
      ({
        ...pinned,
        holdings: [],
        marketValueCents: pinned.marketValueCents ?? 0,
        subAccounts: [],
        expectedCashPercentage: 0,
      } as unknown as PortfolioReport);

    const { priority, rank } = classifyPriority(reportForPriority);
    return {
      ...reportForPriority,
      _priority: priority,
      _priorityRank: rank,
      _drift: calculateDrift(reportForPriority) ?? 0,
    };
  }, [
    pinnedPortfolioDeepLink,
    expandedPortfolioId,
    portfolios,
    hydratedPinnedReport,
  ]);

  // Scroll pinned portfolio into view once the row (or ghost stub) is rendered.
  useEffect(() => {
    if (!pinnedPortfolioDeepLink) {
      scrolledPinRef.current = null;
      return;
    }
    const targetId = pinnedPortfolioDeepLink.portfolioReportId;
    if (scrolledPinRef.current === targetId) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tryScroll = () => {
      const el = document.getElementById(`portfolio-row-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrolledPinRef.current = targetId;
      } else if (attempts < 15) {
        attempts++;
        timer = setTimeout(tryScroll, 120);
      }
    };
    timer = setTimeout(tryScroll, 120);
    return () => clearTimeout(timer);
  }, [pinnedPortfolioDeepLink]);

  // Defer expensive calculations until the user actually needs them
  const aggregateExposures = useMemo(
    () =>
      viewMode === 'exposure'
        ? calculateAggregateExposure(filteredPortfolios)
        : [],
    [filteredPortfolios, viewMode],
  );

  const modelAggregations = useMemo(
    () =>
      viewMode === 'model' ? calculateModelAggregation(filteredPortfolios) : [],
    [filteredPortfolios, viewMode],
  );

  const [modelStagingPortfolios, setModelStagingPortfolios] = useState<
    any[] | null
  >(null);

  const handleBulkOptimize = async (portfolioIds: string[]) => {
    try {
      const promises = portfolioIds.map((portfolioReportId) =>
        runOptimizer({
          variables: {
            input: {
              organizationId: activeOrganization?.id,
              portfolioReportId,
              dryRun: false,
            },
          },
        }),
      );
      await Promise.all(promises);
      showToast({
        severity: 'success',
        message: `Started rebalancing ${portfolioIds.length} portfolio${portfolioIds.length !== 1 ? 's' : ''}. View progress in Active Operations tab.`,
      });
      setSelectedPortfolios(new Set());
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  // Stable callback for PortfolioRow trade completion
  const handleRowTradeComplete = useCallback(() => {
    setLastBulkCreationTime(Date.now());
    setActiveFlowStep('verifying');
    triggerRefresh();
  }, [setLastBulkCreationTime, setActiveFlowStep, triggerRefresh]);

  if (isPageLoading && portfolios.length === 0 && !previousData)
    return (
      <Box>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Box key={i} display='flex' gap={2} px={2} py={1.5}>
            <Skeleton variant='rectangular' width={18} height={18} />
            <Skeleton variant='text' sx={{ flex: 1 }} />
            <Skeleton variant='text' width='10%' />
            <Skeleton variant='text' width='8%' />
            <Skeleton variant='text' width='12%' />
            <Skeleton variant='text' width='10%' />
          </Box>
        ))}
      </Box>
    );
  // Only block the whole tab when we have a hard error AND no previously loaded
  // page to fall back to (i.e. first load failed). Page-level errors that hit
  // mid-pagination are handled in-table below so the user can still navigate
  // away from the broken page without losing the whole tab.
  if (error && portfolios.length === 0 && !previousData)
    return (
      <Box p={2}>
        <Typography variant='labelSmall' sx={{ color: sys.color.negative }}>
          Error loading portfolios: {error.message}
        </Typography>
      </Box>
    );

  return (
    <Box>
      {selectedPortfolios.size > 0 && !showReviewCommit && (
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={1}
          px={1.5}
          py={0.75}
          sx={{
            border: '1px solid',
            borderColor: sys.color.primary,
            borderRadius: '4px',
            bgcolor: sys.color.primaryVariant,
          }}
        >
          <Typography variant='labelSmall'>
            <strong>{selectedPortfolios.size}</strong> portfolio
            {selectedPortfolios.size > 1 ? 's' : ''} selected
          </Typography>
          <Box display='flex' gap={0.75}>
            <Button
              label='Rebalance Selected'
              variant='filled'
              size='sm'
              onClick={() => {
                setActiveFlowStep('staging');
                setShowBulkStaging(true);
              }}
            />
            <Button
              label='Review & Rebalance'
              variant='outlined'
              size='sm'
              onClick={() => setShowReviewCommit(true)}
            />
            <Button
              label='Clear'
              variant='text'
              size='sm'
              onClick={() => setSelectedPortfolios(new Set())}
            />
          </Box>
        </Box>
      )}
      {showReviewCommit && (
        <Box mb={1}>
          <ReviewAndCommit
            selectedPortfolios={filteredPortfolios.filter((p: any) =>
              selectedPortfolios.has(p.id),
            )}
            operationType='optimize'
            onCommit={(portfolioIds, _auditNote) => {
              handleBulkOptimize(portfolioIds);
              setShowReviewCommit(false);
            }}
            onCancel={() => setShowReviewCommit(false)}
          />
        </Box>
      )}
      {showBulkStaging && selectedPortfolios.size > 0 && (
        <BulkTradeStaging
          selectedPortfolios={filteredPortfolios.filter((p: any) =>
            selectedPortfolios.has(p.id),
          )}
          organizationId={activeOrganization?.id || ''}
          onClose={() => {
            setShowBulkStaging(false);
            setActiveFlowStep('idle');
          }}
          onTradesCreated={() => {
            setShowBulkStaging(false);
            setSelectedPortfolios(new Set());
            setActiveFlowStep('verifying');
            setCurrentTab('tradeRequests');
            triggerRefresh();
          }}
        />
      )}

      <Box
        sx={{
          ...BORDERED_BOX_SX,
          borderRadius: '4px 4px 0 0',
          bgcolor: sys.color.surface,
          overflow: 'hidden',
        }}
      >
        {/* Row 1: View toggle + Priority chips + Actions */}
        <Box
          display='flex'
          alignItems='center'
          gap={0}
          px={1}
          py={0.75}
          sx={{
            borderBottom: '1px solid',
            borderColor: sys.color.outlineVariant,
          }}
        >
          <Box
            display='flex'
            mr={2}
            sx={{ ...BORDERED_BOX_SX, borderRadius: '3px', overflow: 'hidden' }}
          >
            {VIEW_MODE_TOGGLES.map((mode) => (
              <Box
                key={mode.key}
                onClick={() => setViewMode(mode.key)}
                sx={{
                  px: 1.25,
                  py: 0.25,
                  cursor: 'pointer',
                  userSelect: 'none',
                  bgcolor:
                    viewMode === mode.key ? sys.color.primary : 'transparent',
                  '&:hover': {
                    bgcolor:
                      viewMode === mode.key
                        ? sys.color.primary
                        : sys.color.neutralHover,
                  },
                  transition: 'all 0.1s',
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...BOLD_SX,
                    textTransform: 'uppercase',
                    color:
                      viewMode === mode.key
                        ? sys.color.onPrimary
                        : sys.color.onSurfaceVariant,
                  }}
                >
                  {mode.getLabel(filteredPortfolios.length)}
                </Typography>
              </Box>
            ))}
          </Box>

          {getPriorityChips(priorityCounts, sys).map((chip) => (
            <Box
              key={chip.key}
              onClick={() => handlePriorityFilter(chip.key)}
              sx={{
                px: chip.key === 'ALL' ? 1 : 0.75,
                py: 0.25,
                mx: 0.25,
                borderRadius: '3px',
                cursor: 'pointer',
                userSelect: 'none',
                bgcolor:
                  activePriorityFilter === chip.key ? chip.bg : 'transparent',
                border: '1px solid',
                borderColor:
                  activePriorityFilter === chip.key
                    ? chip.color
                    : 'transparent',
                opacity: chip.count === 0 && chip.key !== 'ALL' ? 0.35 : 1,
                '&:hover': { bgcolor: chip.bg },
                transition: 'all 0.1s',
              }}
            >
              <Typography
                variant='labelSmall'
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: chip.color,
                }}
              >
                {chip.label}
              </Typography>
            </Box>
          ))}

          <Box flex={1} />

          {/* Export */}
          <Box
            onClick={() => exportPortfoliosToCsv(filteredPortfolios)}
            sx={{
              px: 1,
              py: 0.25,
              cursor: 'pointer',
              '&:hover': { color: sys.color.onSurface },
            }}
          >
            <Typography
              variant='labelSmall'
              sx={{ fontWeight: 600, ...SECONDARY_TEXT_SX }}
            >
              Export CSV
            </Typography>
          </Box>
        </Box>

        {/* Row 2: Inline filters — tight, cohesive toolbar */}
        <Box
          display='flex'
          alignItems='center'
          gap={0.75}
          px={1}
          py={0.5}
          sx={{
            borderBottom: '1px solid',
            borderColor: sys.color.outlineVariant,
            bgcolor: sys.color.background,
          }}
        >
          {/* Search */}
          <Box sx={{ width: 200, flexShrink: 0 }}>
            <TextField
              label=''
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
              placeholder={
                viewMode === 'portfolios'
                  ? 'Search portfolios...'
                  : 'Search tickers...'
              }
              size='small'
              sx={COMPACT_TEXT_FIELD_SX}
            />
          </Box>

          <Box display='flex' alignItems='center' gap={0.5}>
            <Box sx={{ minWidth: 130 }}>
              <SelectField
                label=''
                value={statusFilter}
                onChange={createFilterChange(setStatusFilter)}
                size='small'
                sx={COMPACT_SELECT_FIELD_SX}
              >
                <MenuItem value='ANY'>All Statuses</MenuItem>
                <MenuItem value='ANY_REBALANCE'>Needs Rebalance</MenuItem>
                <MenuItem value='DRIFT'>Drift</MenuItem>
                <MenuItem value='TRANSFER'>Transfer</MenuItem>
                <MenuItem value='LIQUIDATE'>Liquidation</MenuItem>
                <MenuItem value='PENDING_TRADES'>Trading</MenuItem>
              </SelectField>
            </Box>
            <Box sx={{ minWidth: 110 }}>
              <SelectField
                label=''
                value={portfolioTypeFilter}
                onChange={createFilterChange(setPortfolioTypeFilter)}
                size='small'
                sx={COMPACT_SELECT_FIELD_SX}
              >
                <MenuItem value='ANY'>All Types</MenuItem>
                <MenuItem value='GOAL'>Goals</MenuItem>
                <MenuItem value='SUB_ACCOUNT'>Sub-Accounts</MenuItem>
              </SelectField>
            </Box>
            {modelOptions.length > 1 && (
              <Box sx={{ minWidth: 120 }}>
                <SelectField
                  label=''
                  value={modelFilter}
                  onChange={createFilterChange(setModelFilter)}
                  size='small'
                  sx={COMPACT_SELECT_FIELD_SX}
                >
                  <MenuItem value='ANY'>All Models</MenuItem>
                  {modelOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </SelectField>
              </Box>
            )}
            {currencyOptions.length > 1 && (
              <Box sx={{ minWidth: 90 }}>
                <SelectField
                  label=''
                  value={currencyFilter}
                  onChange={createFilterChange(setCurrencyFilter)}
                  size='small'
                  sx={COMPACT_SELECT_FIELD_SX}
                >
                  <MenuItem value='ANY'>All CCY</MenuItem>
                  {currencyOptions.map((ccy) => (
                    <MenuItem key={ccy} value={ccy}>
                      {ccy}
                    </MenuItem>
                  ))}
                </SelectField>
              </Box>
            )}

            {(statusFilter !== 'ANY' ||
              portfolioTypeFilter !== 'ANY' ||
              modelFilter !== 'ANY' ||
              currencyFilter !== 'ANY' ||
              searchTerm ||
              activePriorityFilter !== 'ALL') && (
              <Typography
                variant='labelSmall'
                onClick={handleClearFilters}
                sx={{
                  color: sys.color.primary,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear
              </Typography>
            )}
          </Box>

          {/* Spacer */}
          <Box flex={1} />

          <Box display='flex' alignItems='center' gap={1}>
            <Box sx={{ minWidth: 68 }}>
              <SelectField
                label=''
                value={String(pageSize)}
                onChange={createFilterChange(setPageSize, Number)}
                size='small'
                sx={COMPACT_SELECT_FIELD_SX}
              >
                <MenuItem value='25'>25</MenuItem>
                <MenuItem value='50'>50</MenuItem>
                <MenuItem value='100'>100</MenuItem>
              </SelectField>
            </Box>
            <Typography
              variant='labelSmall'
              sx={{
                ...TABULAR_SX,
                color: sys.color.onDisabled,
                whiteSpace: 'nowrap',
              }}
            >
              {visibleRowCount === displayedPortfolios.length
                ? debouncedSearch
                  ? `${displayedPortfolios.length} / ${filteredPortfolios.length}`
                  : `${displayedPortfolios.length} / ${totalCount}`
                : `${visibleRowCount} / ${displayedPortfolios.length}`}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          ...BORDERED_BOX_SX,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          bgcolor: sys.color.surface,
          overflowX: 'auto',
        }}
      >
        {viewMode === 'portfolios' ? (
          /* ── Portfolios Table ── */
          <>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th, & td': {
                      padding: densityConfig.cellPadding,
                      ...SECTION_HEADER_SX,
                    },
                  }}
                >
                  <TableCell sx={{ width: '36px' }}>
                    <Checkbox
                      checked={
                        selectedPortfolios.size ===
                          displayedPortfolios.length &&
                        displayedPortfolios.length > 0
                      }
                      onChange={(checked: boolean) => {
                        if (checked)
                          setSelectedPortfolios(
                            new Set(displayedPortfolios.map((r: any) => r.id)),
                          );
                        else setSelectedPortfolios(new Set());
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '28px' }} />
                  {PORTFOLIO_HEADER_COLS.map((col) => {
                    const column = portfolioTable.getColumn(col.id);
                    return (
                      <TableCell
                        key={col.id}
                        {...(col.right ? { right: true } : {})}
                        {...(col.center ? { center: true } : {})}
                        sx={{
                          width: col.width,
                          ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                          ...SORTABLE_HEADER_SX,
                        }}
                        onClick={column?.getToggleSortingHandler()}
                      >
                        {col.label}
                        <SortIndicator
                          sorted={column?.getIsSorted() || false}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell sx={{ width: '140px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  opacity: isPageLoading ? 0.5 : 1,
                  pointerEvents: isPageLoading ? 'none' : 'auto',
                  transition: 'opacity 0.15s',
                }}
              >
                {displayedPortfolios.length === 0 && isPageLoading && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Box display='flex' justifyContent='center' py={2}>
                        <Skeleton
                          variant='rectangular'
                          width='80%'
                          height={16}
                          sx={{ mx: 'auto' }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {displayedPortfolios.length === 0 &&
                  isPageReady &&
                  hasPageLoadError && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Box
                          display='flex'
                          flexDirection='column'
                          alignItems='center'
                          gap={1}
                          py={3}
                          px={2}
                        >
                          <Typography
                            variant='labelSmall'
                            sx={{
                              color: sys.color.negative,
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            This page failed to load.
                          </Typography>
                          <Typography
                            variant='labelSmall'
                            sx={{
                              ...SECONDARY_TEXT_SX,
                              textAlign: 'center',
                              maxWidth: 640,
                            }}
                          >
                            The backend returned a GraphQL non-null violation
                            for one or more rows on page {page}, so the entire
                            page payload was dropped. Other pages may still
                            work.
                          </Typography>
                          {pageGraphQLErrors.length > 0 && (
                            <Typography
                              variant='labelSmall'
                              sx={{
                                ...SECONDARY_TEXT_SX,
                                fontFamily: 'monospace',
                                textAlign: 'center',
                                maxWidth: 720,
                                wordBreak: 'break-word',
                              }}
                            >
                              {pageGraphQLErrors.join(' • ')}
                            </Typography>
                          )}
                          <Box display='flex' gap={1} mt={0.5}>
                            <Button
                              label='Retry this page'
                              variant='outlined'
                              size='sm'
                              onClick={() => refetch()}
                            />
                            {page > 1 && (
                              <Button
                                label='Go back a page'
                                variant='text'
                                size='sm'
                                onClick={() =>
                                  startTransition(() =>
                                    setPage((p) => Math.max(1, p - 1)),
                                  )
                                }
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                {displayedPortfolios.length === 0 &&
                  isPageReady &&
                  !hasPageLoadError && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography
                          variant='labelSmall'
                          sx={{
                            ...SECONDARY_TEXT_SX,
                            p: 3,
                            textAlign: 'center',
                          }}
                        >
                          {unresolvedRowCount > 0
                            ? `${unresolvedRowCount} portfolio${unresolvedRowCount === 1 ? '' : 's'} on this page could not be loaded.`
                            : 'No portfolios found. '}
                          {unresolvedRowCount === 0 &&
                            (searchTerm ||
                            statusFilter !== 'ANY' ||
                            portfolioTypeFilter !== 'ANY' ||
                            currencyFilter !== 'ANY'
                              ? 'Try adjusting your filters.'
                              : 'No data available.')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                {/* Ghost pinned row — injected from dashboard data when the
                    deep-link target is not on the current loaded page */}
                {ghostReport && (
                  <PortfolioRow
                    key={`ghost-${ghostReport.id}`}
                    report={ghostReport}
                    isPinned
                    selected={false}
                    onToggleSelect={() => {}}
                    isExpanded={expandedPortfolioId === ghostReport.id}
                    onSetExpanded={handleSetExpanded}
                    organizationId={activeOrganization?.id || ''}
                    bulkTradingProcess={activeOrganization?.bulkTradingProcess}
                    onTradeComplete={handleRowTradeComplete}
                  />
                )}
                {rowsToRender.map((row) => {
                  const report = row.original;
                  return (
                    <PortfolioRow
                      key={report.id}
                      report={report}
                      isPinned={
                        pinnedPortfolioDeepLink?.portfolioReportId === report.id
                      }
                      selected={selectedPortfolios.has(report.id)}
                      onToggleSelect={handleToggleSelect}
                      isExpanded={expandedPortfolioId === report.id}
                      onSetExpanded={handleSetExpanded}
                      organizationId={activeOrganization?.id || ''}
                      bulkTradingProcess={
                        activeOrganization?.bulkTradingProcess
                      }
                      onTradeComplete={handleRowTradeComplete}
                    />
                  );
                })}
                {(isPageLoading || effectiveRenderCount < visibleRowCount) && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Box display='flex' justifyContent='center' py={1}>
                        <Skeleton
                          variant='rectangular'
                          width='80%'
                          height={16}
                          sx={{ mx: 'auto' }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <Box display='flex' justifyContent='center' py={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_: any, newPage: number) =>
                    startTransition(() => setPage(newPage))
                  }
                />
              </Box>
            )}
          </>
        ) : viewMode === 'exposure' ? (
          <CrossPortfolioExposure
            exposures={aggregateExposures}
            searchTerm={searchTerm}
            density={density}
            onCreateTrades={handleCreateTradesFromExposure}
          />
        ) : (
          <ModelPortfolioView
            models={modelAggregations}
            searchTerm={searchTerm}
            onRebalanceSelected={(portfolios) =>
              setModelStagingPortfolios(portfolios)
            }
          />
        )}
      </Box>

      {modelStagingPortfolios && modelStagingPortfolios.length > 0 && (
        <BulkTradeStaging
          selectedPortfolios={modelStagingPortfolios}
          organizationId={activeOrganization?.id || ''}
          onClose={() => {
            setModelStagingPortfolios(null);
            setActiveFlowStep('idle');
          }}
          onTradesCreated={() => {
            setModelStagingPortfolios(null);
            setActiveFlowStep('verifying');
            setCurrentTab('tradeRequests');
            triggerRefresh();
          }}
        />
      )}
    </Box>
  );
};

export default PortfoliosTab;
